import { CommonModule } from '@angular/common';
import { Component, computed, Inject, inject, OnDestroy, Optional, Signal, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import {
    CalendarFormState,
    CalendarGroup,
    CalendarGroupFormState,
    CalendarIntegration,
    FormOperationStatus
} from '@app/_interfaces/calendar-integration.interface';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { getNewId, isMevolveCalendar, isUserMevolveCalendar, getMevolveCalendarDisplayName } from '@app/_utils/utils';
import { getNormalizedCurrentTimezone } from '@app/_services/date';
import { InputCheckboxComponent } from '@app/components/shared/inputs/input-checkbox/input-checkbox.component';
import { CalendarIntegrationStore, HabitStore, JournalStore, FocusStore, MoneyTrackerStore } from '@app/_stores';
import { ConsolidatedApiService } from '@app/_services/consolidated-api.service';
import { CalendarIntegrationType } from '@app/_types/generic.type';
import { CacheService } from '@app/_services/cache.service';
import { AlertService } from '@app/_services/alert.service';
import { FirestoreCollection } from '@app/_enums/firestore-collection.enum';
import { EntityVersions } from '@app/_configs/entity-versions';
import { SettingsService } from '@app/_services/settings.service';
import { AnalyticsService } from '@app/_services/analytics.service';
import { MapService } from '@app/_services/map.service';
import { AnalyticsScreen, SubFeatureActionValue } from '@app/_types/analytics-events.types';
import { StorageService } from '@app/_services/storage.servive';
import { DependencyService } from '@app/_services/dependency.service';
import { IndexDbService } from '@app/_services/index-db.service';
import { CloudSyncService } from '@app/_services/cloud-sync.service';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { ModalService } from '@app/_services/modal.service';
import { FeatureGuideDialogComponent } from '@app/components/common/feature-guides/feature-guide-dialog/feature-guide-dialog.component';
import { log } from '@app/_services/logger';
import { CalendarIntegrationService } from '@app/_services/features/calendar-integration.service';
import { BRAND_CONFIG } from '@app/_generated/brand.gen';

@Component({
    selector: 'app-calendar-account-form',
    imports: [
        CommonModule,
        FormsModule,
        MatMenuModule,
        SvgComponent,
        InputCheckboxComponent
    ],
    templateUrl: './calendar-account-form.component.html',
    styleUrl: './calendar-account-form.component.scss'
})
export class CalendarAccountFormComponent implements OnDestroy, OnInit {

    readonly BRAND_CONFIG = BRAND_CONFIG;

    /** Timeout duration for sync operations (10 minutes in milliseconds) */
    private readonly SYNC_TIMEOUT_MS = 600000;

    unSubscribe = new Subject<void>();

    // Calendar account display info (read-only)
    calendarAccountInfo: { id: string; name: string; email: string };

    // Dual-config pattern: originalState is snapshot at open, currentState tracks user edits
    private originalState!: CalendarFormState;
    currentState = signal<CalendarFormState>({
        syncAllScheduledTodos: false,
        todoReminderMode: 'sync',
        syncedItems: [],
        calendarGroups: []
    });

    // Computed hasChanges - compares originalState vs currentState
    hasChanges = computed(() => {
        return !this.deepEqual(this.originalState, this.currentState());
    });

    // Operation status for UI feedback
    operationStatus = signal<FormOperationStatus>({ type: 'idle' });
    isSyncing = computed(() => this.operationStatus().type === 'syncing');
    isSaving = computed(() => this.operationStatus().type === 'saving');

    // For async calendar sync status watching via RTDB-driven IDB updates
    private statusSubscription: Subscription | null = null;
    private statusWatchTimeout: ReturnType<typeof setTimeout> | null = null;

    readonly calendarStore = inject(CalendarIntegrationStore);
    private readonly habitStore = inject(HabitStore);
    private readonly journalStore = inject(JournalStore);
    private readonly focusStore = inject(FocusStore);
    private readonly moneyTrackerStore = inject(MoneyTrackerStore);

    /** Filtered setups for per-setup calendar sync toggles (matches Flutter filter: deletedAt == null && uid == currentUid && startDate != null) */
    scheduledHabitSetups = computed(() => {
        const uid = this.cc.user?.uid;
        return this.habitStore.setups().filter(s => !s.deletedAt && s.uid === uid && s.startDate != null);
    });
    scheduledJournalSetups = computed(() => {
        const uid = this.cc.user?.uid;
        return this.journalStore.setups().filter(s => !s.deletedAt && s.uid === uid && s.startDate != null);
    });
    scheduledFocusSetups = computed(() => {
        const uid = this.cc.user?.uid;
        return this.focusStore.setups().filter(s => !s.deletedAt && s.uid === uid && s.startDate != null);
    });
    /** Money tracker: setups with at least one active scheduled schedule */
    scheduledMoneyTrackerSetups = computed(() => {
        const uid = this.cc.user?.uid;
        return this.moneyTrackerStore.setups()
            .filter(s => !s.deletedAt && s.uid === uid)
            .map(setup => {
                const schedules = Object.values(setup.schedules || {})
                    .filter(sch => !sch.deletedAt && sch.startDate != null);
                return { setup, schedules };
            })
            .filter(item => item.schedules.length > 0);
    });

    calendarType: CalendarIntegrationType;
    screenEnterTime: Signal<Date> = signal(new Date());
    calendarTypeString: Signal<SubFeatureActionValue> = computed(() => {
        return this.mapService.subTypesMap()[this.calendarType];
    });

    data: { mode: 'new' | 'edit', value: CalendarIntegration, autoReAuthenticate?: boolean };

    constructor(
        @Optional() private dialogRef: MatDialogRef<CalendarAccountFormComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: { mode: 'new' | 'edit', value: Signal<CalendarIntegration>, autoReAuthenticate?: boolean },
        @Optional() private bottomSheetRef: CustomBottomSheetRef<CalendarAccountFormComponent>,
        @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) public bottomSheetData: { mode: 'new' | 'edit', value: Signal<CalendarIntegration>, autoReAuthenticate?: boolean },
        private consolidatedApi: ConsolidatedApiService,
        public cc: CacheService,
        private alertService: AlertService,
        public ss: SettingsService,
        private as: AnalyticsService,
        private mapService: MapService,
        private storageService: StorageService,
        public ds: DependencyService,
        private idbService: IndexDbService,
        private cloudSyncService: CloudSyncService,
        private modalService: ModalService,
        private calendarService: CalendarIntegrationService,
    ) {
        const selectedData = dialogData || bottomSheetData;
        this.data = {
            mode: selectedData.mode,
            value: typeof selectedData.value === 'function' ? selectedData.value() : selectedData.value,
            autoReAuthenticate: selectedData.autoReAuthenticate
        };
        this.calendarType = this.data.value.calendarType;

        // Extract display info
        this.calendarAccountInfo = {
            id: this.data.value.id,
            name: this.data.value.name,
            email: this.data.value.email
        };

        // Initialize dual-config state
        this.initializeState(this.data.value);

        dialogRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
            this.closeDialog();
        });
    }

    ngOnInit() {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('calendarSetup', previousScreen, this.calendarTypeString());
        if (this.data.mode === 'edit') {
            this.as.crudEvent('read', 'calendarSetup', 'private', 0, 'notShared', 'owner', this.calendarTypeString());
        }

        // Check if calendar is already syncing and start watching status
        if (this.data.mode === 'edit' && (this.data.value.status === 'syncing')) {
            log.info('CalendarAccountForm', 'Calendar is already syncing, watching status', { calendarId: this.data.value.id, status: this.data.value.status });
            this.operationStatus.set({ type: 'syncing' });
            this.watchSyncStatus(this.data.value.id, false, new Date());
        }

        // Auto-trigger re-authentication if opened from notification
        if (this.data.autoReAuthenticate) {
            setTimeout(() => this.reAuthenticate(), 0);
        }
    }

    /**
     * Initialize the dual-config state from the calendar integration.
     * Creates both originalState (snapshot) and currentState (for editing).
     */
    private initializeState(calendarAccount: CalendarIntegration): void {
        // Filter out only the CURRENT USER's Mevolve calendar from "Calendar → Mevolve" section
        // Shared Mevolve calendars from other users should still be shown
        const calendarGroups: CalendarGroupFormState[] = (calendarAccount.calendarGroups || [])
            .filter(group => !isUserMevolveCalendar(group.title, this.cc.user?.email))
            .map(group => ({
                id: group.id,
                title: group.title,
                isPrimary: group.isPrimary ?? false,
                // For new integrations, enable all calendars by default (matches Flutter)
                syncStatus: this.data.mode === 'new'
                    ? 'syncWithReminders'
                    : group.syncStatus
            }));

        const state: CalendarFormState = {
            syncAllScheduledTodos: calendarAccount.syncAllScheduledTodos,
            todoReminderMode: calendarAccount.todoReminderMode,
            syncedItems: calendarAccount.syncedItems,
            calendarGroups: calendarGroups
        };

        // Store original state (deep clone for comparison)
        this.originalState = JSON.parse(JSON.stringify(state));

        // Set current state
        this.currentState.set(state);
    }

    /**
     * Toggle Mevolve->Calendar sync for a specific entity type.
     * Cycles between disabled <-> syncWithReminders.
     */
    private _typeToCollection(type: string): string {
        switch (type) {
            case 'habits': return 'habitSetups';
            case 'journals': return 'journalSetups';
            case 'focus': return 'focusSetups';
            case 'moneyTracker': return 'moneyTrackerSetups';
            default: return type;
        }
    }

    toggleMevolveSync(type: 'todos' | 'habits' | 'journals' | 'focus' | 'moneyTracker'): void {
        if (type === 'todos') {
            this.currentState.update(state => ({
                ...state,
                syncAllScheduledTodos: !state.syncAllScheduledTodos,
                todoReminderMode: !state.syncAllScheduledTodos ? 'syncWithReminders' : 'sync'
            }));
        }
        // Type-level toggle is no longer used for habits/journals/focus/moneyTracker.
        // Per-setup toggles are used instead via toggleSetupSync().
    }

    /**
     * Toggle sync for an individual setup (per-setup granularity, matching Flutter).
     */
    toggleSetupSync(type: 'habits' | 'journals' | 'focus' | 'moneyTracker', setupId: string, scheduleId: string | null = null): void {
        const collection = this._typeToCollection(type);
        this.currentState.update(state => {
            const exists = state.syncedItems.some(i =>
                i.collection === collection && i.setupId === setupId && i.scheduleId === scheduleId
            );
            return {
                ...state,
                syncedItems: exists
                    ? state.syncedItems.filter(i => !(i.collection === collection && i.setupId === setupId && i.scheduleId === scheduleId))
                    : [...state.syncedItems, { collection, setupId, scheduleId, reminders: true }]
            };
        });
    }

    /**
     * Toggle reminder mode for Mevolve->Calendar sync.
     * For todos: cycles todoReminderMode. For setups: toggles reminders on specific syncedItem.
     */
    toggleMevolveReminder(type: 'todos' | 'habits' | 'journals' | 'focus' | 'moneyTracker'): void {
        if (type === 'todos') {
            if (!this.currentState().syncAllScheduledTodos) return;
            this.currentState.update(state => ({
                ...state,
                todoReminderMode: state.todoReminderMode === 'syncWithReminders' ? 'sync' : 'syncWithReminders'
            }));
        }
        // Per-setup reminder toggle is handled by toggleSetupReminder().
    }

    /**
     * Toggle reminder for an individual setup/schedule sync item.
     */
    toggleSetupReminder(type: 'habits' | 'journals' | 'focus' | 'moneyTracker', setupId: string, scheduleId: string | null = null): void {
        const collection = this._typeToCollection(type);
        const item = this.currentState().syncedItems.find(i =>
            i.collection === collection && i.setupId === setupId && i.scheduleId === scheduleId
        );
        if (!item) return;
        this.currentState.update(state => ({
            ...state,
            syncedItems: state.syncedItems.map(i =>
                (i.collection === collection && i.setupId === setupId && i.scheduleId === scheduleId)
                    ? { ...i, reminders: !i.reminders }
                    : i
            )
        }));
    }

    /**
     * Toggle Calendar->Mevolve sync for a specific calendar group.
     * Cycles between disabled <-> syncWithReminders.
     */
    toggleCalendarGroup(groupId: string): void {
        this.currentState.update(state => ({
            ...state,
            calendarGroups: state.calendarGroups.map(g =>
                g.id === groupId
                    ? { ...g, syncStatus: g.syncStatus === 'disabled' ? 'syncWithReminders' : 'disabled' }
                    : g
            )
        }));
    }

    /**
     * Toggle reminder mode for a calendar group.
     * Cycles between syncWithReminders <-> sync (when already enabled).
     */
    toggleCalendarGroupReminder(groupId: string): void {
        const group = this.currentState().calendarGroups.find(g => g.id === groupId);
        if (!group || group.syncStatus === 'disabled') return;

        this.currentState.update(state => ({
            ...state,
            calendarGroups: state.calendarGroups.map(g =>
                g.id === groupId
                    ? { ...g, syncStatus: g.syncStatus === 'syncWithReminders' ? 'sync' : 'syncWithReminders' }
                    : g
            )
        }));
    }

    /**
     * Check if a Mevolve->Calendar sync type is enabled.
     * For todos: checks syncAllScheduledTodos flag.
     * For per-setup types: checks if a specific setupId (and optional scheduleId) is in syncedItems.
     */
    isMevolveSyncEnabled(type: 'todos' | 'habits' | 'journals' | 'focus' | 'moneyTracker', setupId?: string, scheduleId?: string | null): boolean {
        if (type === 'todos') return this.currentState().syncAllScheduledTodos;
        const collection = this._typeToCollection(type);
        if (setupId) {
            return this.currentState().syncedItems.some(i =>
                i.collection === collection && i.setupId === setupId && i.scheduleId === (scheduleId ?? null)
            );
        }
        return this.currentState().syncedItems.some(i => i.collection === collection);
    }

    /**
     * Check if reminders are enabled for a Mevolve->Calendar sync type.
     * For per-setup types: checks the specific setupId/scheduleId entry.
     */
    hasMevolveReminders(type: 'todos' | 'habits' | 'journals' | 'focus' | 'moneyTracker', setupId?: string, scheduleId?: string | null): boolean {
        if (type === 'todos') return this.currentState().todoReminderMode === 'syncWithReminders';
        const collection = this._typeToCollection(type);
        if (setupId) {
            const item = this.currentState().syncedItems.find(i =>
                i.collection === collection && i.setupId === setupId && i.scheduleId === (scheduleId ?? null)
            );
            return item?.reminders ?? false;
        }
        const item = this.currentState().syncedItems.find(i => i.collection === collection);
        return item?.reminders ?? false;
    }

    /**
     * Check if a calendar group sync is enabled.
     */
    isCalendarGroupEnabled(groupId: string): boolean {
        const group = this.currentState().calendarGroups.find(g => g.id === groupId);
        return group ? group.syncStatus !== 'disabled' : false;
    }

    /**
     * Check if reminders are enabled for a calendar group.
     */
    hasCalendarGroupReminders(groupId: string): boolean {
        const group = this.currentState().calendarGroups.find(g => g.id === groupId);
        return group ? group.syncStatus === 'syncWithReminders' : false;
    }

    /**
     * Get display name for a calendar group.
     * If it's a Mevolve calendar from another user, show full name.
     * If it's current user's Mevolve calendar, show localized text.
     */
    getCalendarGroupDisplayName(title: string): string {
        if (isMevolveCalendar(title)) {
            return getMevolveCalendarDisplayName(
                title,
                this.cc.user.email,
                this.ss.t('calendarAccount_brandToCalendar', { brandName: BRAND_CONFIG.name })
            );
        }
        return title;
    }

    /**
     * Deep equality check for CalendarFormState.
     * Uses JSON comparison for simplicity and reliability.
     */
    private deepEqual(a: CalendarFormState, b: CalendarFormState): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    /**
     * Build the calendar groups array for the backend.
     * Merges form state back into the original calendar groups.
     */
    private buildCalendarGroups(): CalendarGroup[] {
        const originalGroups = this.data.value.calendarGroups || [];
        const formGroups = this.currentState().calendarGroups;

        return originalGroups.map(original => {
            // Find matching form state (only for non-Mevolve groups)
            const formGroup = formGroups.find(fg => fg.id === original.id);

            if (formGroup) {
                // Update syncStatus from form state
                return { ...original, syncStatus: formGroup.syncStatus };
            }

            // Keep Mevolve calendar groups unchanged
            return original;
        });
    }

    async save(isResync = false) {
        // Guard: don't allow save on existing calendars with accountError status
        // (matching Flutter's _updateExistingCalendar guard)
        if (!isResync && this.data.mode === 'edit' && this.data.value.status === 'accountError') {
            return;
        }

        const funcStartTime = new Date();
        this.operationStatus.set({ type: 'saving' });
        this.cc.isLoading.set(true);

        log.info('CalendarAccountForm', 'Starting save', { isResync, mode: this.data.mode });

        try {
            if (isResync) {
                // Resync stays as direct API call (matching Flutter)
                const result = await this.consolidatedApi.call<{ statusCode: number }>('resyncCalendar', {
                    calAccId: this.data.value.id
                });
                if (result.error) {
                    throw new Error(result.error);
                }
                const response = result.data!;

                if (response.statusCode === 202) {
                    log.info('CalendarAccountForm', 'Async resync started, watching for completion');
                    this.operationStatus.set({ type: 'syncing' });
                    this.watchSyncStatus(this.data.value.id, isResync, funcStartTime);
                    return;
                }

                this.as.crudEvent('update', 'calendarSetup', 'private', 0, 'notShared', 'owner', this.calendarTypeString());

                setTimeout(() => {
                    this.operationStatus.set({ type: 'idle' });
                    this.cc.isLoading.set(false);
                    this.close({ mode: this.data.mode, id: this.calendarAccountInfo.id, isResync, success: true });
                }, 1000);
            } else {
                // Create or edit: save via IndexedDB + cloud sync (matching Flutter)
                const isNew = this.data.mode === 'new';
                const updatedAccount = this.buildUpdatedCalendarIntegration();
                if (isNew) {
                    updatedAccount.status = 'syncing';
                }
                const originalAccount = isNew ? null : this.data.value;

                // Save to IndexedDB (upsert handles both new and existing records;
                // for 'new' mode the OAuth flow may have already inserted the record via RTDB sync)
                await this.idbService.upsert('calendarIntegrations', updatedAccount);

                // Trigger cloud sync - cloud sync pushes to backend, backend post-save detects changes and runs orchestrator
                this.cloudSyncService.saveData(
                    FirestoreCollection.CalendarIntegrations,
                    [updatedAccount],
                    originalAccount ? [originalAccount] : []
                );

                this.as.crudEvent(isNew ? 'create' : 'update', 'calendarSetup', 'private', 0, 'notShared', 'owner', this.calendarTypeString());

                // Watch for sync completion
                log.info('CalendarAccountForm', 'Save via cloud sync initiated, watching status');
                this.operationStatus.set({ type: 'syncing' });
                this.watchSyncStatus(updatedAccount.id, false, funcStartTime);
            }
        } catch (error: any) {
            log.error('CalendarAccountForm', 'Calendar save failed', error);
            this.operationStatus.set({ type: 'error', message: error?.message || 'Unknown error' });
            this.cc.isLoading.set(false);

            setTimeout(() => {
                this.close({ mode: this.data.mode, id: this.calendarAccountInfo.id, isResync, success: false });
            }, 2000);
        }
    }

    /**
     * Watch for calendar sync status changes via RTDB-driven IDB updates.
     * Replaces direct Firestore onSnapshot — status changes arrive via
     * RTDB metadata → cloud sync → IDB → calendarIntegrations$ observable.
     */
    private watchSyncStatus(calId: string, isResync: boolean, funcStartTime: Date) {
        log.info('CalendarAccountForm', 'Setting up RTDB-based status watcher', { calId });

        // Clean up any existing watcher
        this.cleanupStatusWatcher();

        this.statusSubscription = this.idbService.calendarIntegrations$.pipe(
            takeUntil(this.unSubscribe),
        ).subscribe((integrations) => {
            if (!integrations) return;

            const cal = integrations.find(c => c.id === calId);
            if (!cal) {
                log.debug('CalendarAccountForm', 'Calendar not found in IDB during status watch', { calId });
                return;
            }

            const status = cal.status;
            log.info('CalendarAccountForm', 'Status update via RTDB sync', { status });

            switch (status) {
                case 'active':
                    log.info('CalendarAccountForm', 'Async sync completed successfully');
                    this.handleSyncSuccess(isResync, funcStartTime);
                    break;

                case 'failed':
                    const syncError = cal.syncError ?? undefined;
                    log.error('CalendarAccountForm', 'Async sync failed', { syncError });
                    this.handleSyncError(isResync, funcStartTime, syncError);
                    break;

                case 'syncing':
                    log.debug('CalendarAccountForm', 'Sync in progress', { status });
                    break;

                default:
                    log.warn('CalendarAccountForm', 'Unexpected status', { status });
            }
        });

        // Timeout fallback
        this.statusWatchTimeout = setTimeout(() => {
            log.warn('CalendarAccountForm', 'Sync timeout - max wait time exceeded');
            this.handleSyncError(isResync, funcStartTime, 'Sync timeout - please check calendar settings');
        }, this.SYNC_TIMEOUT_MS);
    }

    private handleSyncSuccess(isResync: boolean, _funcStartTime: Date) {
        this.cleanupStatusWatcher();
        this.operationStatus.set({ type: 'idle' });
        this.cc.isLoading.set(false);

        this.as.crudEvent(this.data.mode === 'new' ? 'create' : 'update', 'calendarSetup', 'private', 0, 'notShared', 'owner', this.calendarTypeString());

        setTimeout(() => {
            this.close({ mode: this.data.mode, id: this.calendarAccountInfo.id, isResync: isResync, success: true });
        }, 500);
    }

    private handleSyncError(isResync: boolean, _funcStartTime: Date, errorMessage?: string) {
        this.cleanupStatusWatcher();
        this.operationStatus.set({ type: 'error', message: errorMessage || 'Sync failed' });
        this.cc.isLoading.set(false);

        if (errorMessage) {
            log.error('CalendarAccountForm', 'Sync error', { errorMessage });
        }

        setTimeout(() => {
            this.close({ mode: this.data.mode, id: this.calendarAccountInfo.id, isResync: isResync, success: false });
        }, 1000);
    }

    private cleanupStatusWatcher() {
        if (this.statusSubscription) {
            this.statusSubscription.unsubscribe();
            this.statusSubscription = null;
        }
        if (this.statusWatchTimeout) {
            clearTimeout(this.statusWatchTimeout);
            this.statusWatchTimeout = null;
        }
    }

    /**
     * Build the updated CalendarIntegration object from form state.
     * Sets sync flags to mark as pending sync for cloud sync to pick up.
     */
    private buildUpdatedCalendarIntegration(): CalendarIntegration {
        const state = this.currentState();
        const calendarGroups = this.buildCalendarGroups();
        const isNew = this.data.mode === 'new';

        return {
            ...this.data.value,
            syncAllScheduledTodos: state.syncAllScheduledTodos,
            todoReminderMode: state.todoReminderMode,
            syncedItems: state.syncedItems,
            calendarGroups: calendarGroups,
            uid: this.cc.user.uid || '',
            entityType: 'calendarIntegrations',
            entityVer: EntityVersions.calendarIntegrations,
            source: 'client',
            deletedAt: null,
            createdAt: isNew ? new Date() : this.data.value.createdAt,
            sessionId: getNewId(),
            timeZone: this.data.value.timeZone || getNormalizedCurrentTimezone(),
            localUpdatedAt: new Date(),
            cloudUpdatedAt: null as unknown as Date, // Marks as pending sync - server will set with serverTimestamp()
        };
    }

    async deleteCalendar() {
        const res = await this.alertService.confirmRevamped({
            title: this.ss.t('deleteCalendarAccountConfirmation_title'),
            description: this.ss.t('deleteCalendarAccountConfirmation_content'),
            yesButtonText: this.ss.t('common_delete'),
            noButtonText: this.ss.t('common_buttonCancel'),
            yesButtonColor: 'color-11',
            noButtonColor: 'color-7',
        });
        if (!res) return;
        this.deleteCalendarAccount();
    }

    async deleteCalendarAccount() {
        this.operationStatus.set({ type: 'saving' });
        this.cc.isLoading.set(true);

        log.info('CalendarAccountForm', 'Deleting calendar account via cloud sync', { calendarId: this.data.value.id });

        try {
            // Mark as deleted and save via cloud sync (matching Flutter pattern)
            const deletedAccount: CalendarIntegration = {
                ...this.data.value,
                deletedAt: new Date(),
                localUpdatedAt: new Date(),
                cloudUpdatedAt: null as unknown as Date, // Marks as pending sync
            };

            // Save to IndexedDB
            await this.idbService.update('calendarIntegrations', deletedAccount.id, deletedAccount);

            // Trigger cloud sync
            this.cloudSyncService.saveData(
                FirestoreCollection.CalendarIntegrations,
                [deletedAccount],
                [this.data.value]
            );

            this.as.crudEvent('delete', 'calendarSetup', 'private', 0, 'notShared', 'owner', this.calendarTypeString());

            // Immediately clean up local IndexedDB to reflect deletion in UI
            await this.cleanupLocalCalendarData();

            setTimeout(() => {
                this.operationStatus.set({ type: 'idle' });
                this.cc.isLoading.set(false);
                this.close({ mode: 'delete', id: this.data.value.id, success: true });
            }, 500);
        } catch (error: any) {
            log.error('CalendarAccountForm', 'Calendar deletion failed', error);
            this.operationStatus.set({ type: 'idle' });
            this.cc.isLoading.set(false);
            this.close({ mode: 'delete', id: this.data.value.id, success: false, error: error?.message || 'Unknown error' });
        }
    }

    /**
     * Immediately clean up local IndexedDB data after successful calendar deletion.
     * This ensures the UI reflects the deletion immediately without waiting for async RTDB sync.
     */
    private async cleanupLocalCalendarData(): Promise<void> {
        const calendarId = this.data.value.id;
        log.info('CalendarAccountForm', 'Cleaning up local data for calendar', { calendarId });

        try {
            // Get all calendar event setups and actions that belong to this integration
            const eventSetups = this.calendarStore.eventSetups();
            const eventActions = this.calendarStore.events();

            // Filter to find IDs of events belonging to this calendar integration
            const eventSetupIdsToDelete = eventSetups
                .filter(setup => setup.calendarIntegrationId === calendarId)
                .map(setup => setup.id);

            const eventActionIdsToDelete = eventActions
                .filter(action => action.calendarIntegrationId === calendarId)
                .map(action => action.id);

            log.info('CalendarAccountForm', 'Deleting local calendar data', {
                calendarIntegrationId: calendarId,
                eventSetups: eventSetupIdsToDelete.length,
                eventActions: eventActionIdsToDelete.length
            });

            // Delete from IndexedDB - this also triggers emitCollectionToUI for each collection
            await Promise.all([
                this.idbService.bulkDelete('calendarIntegrations', [calendarId]),
                eventSetupIdsToDelete.length > 0
                    ? this.idbService.bulkDelete('calendarEventSetups', eventSetupIdsToDelete)
                    : Promise.resolve([]),
                eventActionIdsToDelete.length > 0
                    ? this.idbService.bulkDelete('calendarEventActions', eventActionIdsToDelete)
                    : Promise.resolve([])
            ]);

            log.info('CalendarAccountForm', 'Local cleanup complete');
        } catch (error) {
            log.error('CalendarAccountForm', 'Local cleanup failed', error as Error);
            // Don't throw - the deletion succeeded via cloud sync, so we should still close the dialog
        }
    }

    /**
     * Re-authenticate a calendar with accountError status.
     * Uses shared CalendarIntegrationService for OAuth flow.
     */
    async reAuthenticate() {
        try {
            let credentials: any;
            if (this.calendarType === 'google') {
                credentials = await this.calendarService.connectGoogle();
            } else {
                credentials = await this.calendarService.connectMicrosoft();
            }

            if (!credentials) return; // User cancelled OAuth

            this.cc.isLoading.set(true);
            this.operationStatus.set({ type: 'saving' });

            const freshIntegration = await this.calendarService.getCalendarDetails(credentials);

            // Update existing integration with fresh tokens + calendar groups
            const updatedAccount: CalendarIntegration = {
                ...this.data.value,
                accessToken: freshIntegration.accessToken,
                refreshToken: freshIntegration.refreshToken,
                calendarGroups: freshIntegration.calendarGroups,
                status: 'active',
                localUpdatedAt: new Date(),
                cloudUpdatedAt: null as unknown as Date,
                entityType: 'calendarIntegrations',
                entityVer: EntityVersions.calendarIntegrations,
                source: 'client',
                sessionId: getNewId(),
            };

            // Save via IndexedDB + cloud sync
            await this.idbService.update('calendarIntegrations', updatedAccount.id, updatedAccount);
            this.cloudSyncService.saveData(
                FirestoreCollection.CalendarIntegrations,
                [updatedAccount],
                [this.data.value]
            );

            // Update local state
            this.data.value = updatedAccount;
            this.initializeState(updatedAccount);

            this.cc.isLoading.set(false);
            this.operationStatus.set({ type: 'idle' });

            await this.alertService.alert(
                'Authentication Successful',
                'Your calendar is now authenticated and sync is now active.',
                this.ss.t('common_ok')
            );
        } catch (error: any) {
            log.error('CalendarAccountForm', 'Re-authentication failed', error);
            this.cc.isLoading.set(false);
            this.operationStatus.set({ type: 'idle' });

            await this.alertService.alert(
                'Authentication Failed',
                'Calendar authentication was unsuccessful. Please remove the calendar account and re-integrate again.',
                this.ss.t('common_ok')
            );
        }
    }

    async closeDialog() {
        if (this.hasChanges()) {
            const res = await this.alertService.confirm(this.ss.t('saveChanges_title'), this.ss.t('common_discardContent'), this.ss.t('common_save'), this.ss.t('common_discard'), 'color-35', 'color-11');
            if (!res) return;
            if (res.confirm === true) {
                this.save();
            } else if (res.confirm === false) {
                this.close();
            }
        } else {
            this.close();
        }
    }

    private close(result?: any): void {
        if (this.dialogRef) {
            this.dialogRef.close(result);
        } else if (this.bottomSheetRef) {
            this.bottomSheetRef.dismiss(result);
        }
    }

    openFeatureGuide() {
        this.modalService.openLevel1(
            FeatureGuideDialogComponent,
            {
                entityType: this.calendarType
            },
            { disableClose: true, autoFocus: false }
        );
    }

    ngOnDestroy() {
        this.as.screenExitEvent('calendarSetup', this.screenEnterTime(), this.calendarTypeString());
        this.cleanupStatusWatcher();
        this.unSubscribe.complete();
        this.unSubscribe.next();
    }
}
